import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Brain,
  ChevronRight,
  ClipboardList,
  ExternalLink,
  FileText,
  ListTodo,
  Sparkles,
  MessageSquare,
} from "lucide-react";
import { motion } from "framer-motion";
import portalApi from "@/lib/portalApi";
import { useStartupPortalAuth } from "@/contexts/StartupPortalAuthContext";

const STATUS_LABEL = {
  draft: "draft",
  submitted: "submitted",
  ai_pre_analysis: "ai_pre_analysis",
  fatma_reviewing: "fatma_reviewing",
  reverted_to_founder: "reverted_to_founder",
  curated: "curated",
  archived: "archived",
};

const STATUS_BADGE = {
  draft: "bg-sandBeige/60 text-cocoa",
  submitted: "bg-sageMint text-cocoa",
  ai_pre_analysis: "bg-lavenderFog text-cocoa",
  fatma_reviewing: "bg-butterCream text-cocoa",
  reverted_to_founder: "bg-roseMilk text-cocoa",
  curated: "bg-skyMist text-cocoa",
  archived: "bg-ivory text-warmGrey",
};

const fadeSection = {
  initial: { opacity: 0, y: 8 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.1 },
};

const formatDate = (iso, lang = "de") => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(lang === "en" ? "en-US" : "de-DE", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const Card = ({ accent = "white", title, eyebrow, children, action, testId }) => (
  <motion.section
    {...fadeSection}
    transition={{ duration: 0.4, ease: "easeOut" }}
    className={`relative rounded-3xl border border-sandBeige bg-white p-6 sm:p-7 shadow-soft transition-shadow hover:shadow-card flex flex-col gap-3`}
    data-testid={testId}
  >
    {eyebrow && <p className="wit-eyebrow text-raspberry">{eyebrow}</p>}
    {(title || action) && (
      <div className="flex items-start justify-between gap-3">
        {title && <h2 className="font-serif text-xl sm:text-2xl text-cocoa leading-tight">{title}</h2>}
        {action}
      </div>
    )}
    <div>{children}</div>
    {accent === "sage" && (
      <div aria-hidden="true" className="absolute top-0 left-0 w-16 h-1.5 rounded-bl-3xl rounded-tr-3xl bg-sageMint" />
    )}
    {accent === "lavender" && (
      <div aria-hidden="true" className="absolute top-0 left-0 w-16 h-1.5 rounded-bl-3xl rounded-tr-3xl bg-lavenderFog" />
    )}
    {accent === "sky" && (
      <div aria-hidden="true" className="absolute top-0 left-0 w-16 h-1.5 rounded-bl-3xl rounded-tr-3xl bg-skyMist" />
    )}
    {accent === "butter" && (
      <div aria-hidden="true" className="absolute top-0 left-0 w-16 h-1.5 rounded-bl-3xl rounded-tr-3xl bg-butterCream" />
    )}
  </motion.section>
);

export default function PortalDashboard() {
  const { t, i18n } = useTranslation();
  const { session, refreshSession } = useStartupPortalAuth();
  const [timeline, setTimeline] = useState([]);

  useEffect(() => {
    let cancelled = false;
    portalApi
      .get("/timeline")
      .then(({ data }) => {
        if (!cancelled) setTimeline(data?.events || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // when the founder lands here, re-fetch their /me once so a fresh upload
    // surfaces an updated AI status without a manual refresh.
    refreshSession().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const me = session?.me;
  const completeness = session?.completeness;
  const firstName = me?.founder_self?.first_name || "";
  const lang = i18n.language?.startsWith("en") ? "en" : "de";

  const status = me?.status || "submitted";
  const statusBadge = STATUS_BADGE[status] || STATUS_BADGE.submitted;

  const deckVersion = me?.current_deck_version || 0;
  const deckUploaded = !!me?.deck_uploaded;

  return (
    <div className="max-w-5xl mx-auto flex flex-col gap-8" data-testid="portal-dashboard">
      <motion.header
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <p className="wit-eyebrow text-raspberry mb-2" data-testid="dashboard-eyebrow">
          {t("portal.dashboard.eyebrow")}
        </p>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-cocoa leading-[1.05]" data-testid="dashboard-headline">
          {t("portal.dashboard.greeting", { name: firstName || t("portal.shell.founder") })}
        </h1>
        <p className="mt-3 text-warmGrey">{t("portal.dashboard.sub")}</p>
      </motion.header>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Status */}
        <Card
          accent="sage"
          eyebrow={t("portal.dashboard.statusEyebrow")}
          title={t(`portal.status.${STATUS_LABEL[status] || "submitted"}`)}
          testId="card-status"
        >
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mb-3 ${statusBadge}`}
            data-testid="status-badge"
          >
            {t(`portal.status.${STATUS_LABEL[status] || "submitted"}`)}
          </span>
          <p className="text-warmGrey leading-relaxed text-sm">
            {t(`portal.dashboard.statusDescription.${STATUS_LABEL[status] || "submitted"}`)}
          </p>
          <p className="text-xs text-warmGrey mt-4">
            {t("portal.dashboard.lastUpdate")}: {formatDate(me?.updated_at, lang)}
          </p>
          <p className="text-xs text-warmGrey mt-1 italic" data-testid="status-next">
            {t("portal.dashboard.next")}: {t(`portal.dashboard.nextSteps.${STATUS_LABEL[status] || "submitted"}`)}
          </p>
        </Card>

        {/* Pitch deck */}
        <Card
          accent="sage"
          eyebrow={t("portal.dashboard.deckEyebrow")}
          title={
            deckUploaded
              ? t("portal.dashboard.deckVersionTitle", { v: deckVersion })
              : t("portal.dashboard.deckNoneTitle")
          }
          action={
            <Link
              to="/portal/startup/decks"
              className="text-xs text-raspberry hover:text-coral inline-flex items-center gap-1"
              data-testid="deck-card-link"
            >
              {deckUploaded ? t("portal.dashboard.deckManage") : t("portal.dashboard.deckUpload")} <ChevronRight size={14} />
            </Link>
          }
          testId="card-deck"
        >
          {deckUploaded ? (
            <p className="text-warmGrey text-sm leading-relaxed">
              {t("portal.dashboard.deckHelp")}
            </p>
          ) : (
            <p className="text-warmGrey text-sm leading-relaxed">
              {t("portal.dashboard.deckEmpty")}
            </p>
          )}
        </Card>

        {/* AI status */}
        <Card
          accent="lavender"
          eyebrow={t("portal.dashboard.aiEyebrow")}
          title={t("portal.dashboard.aiTitle")}
          testId="card-ai"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-lavenderFog flex items-center justify-center text-cocoa">
              <Brain size={18} />
            </div>
            <div>
              <p className="text-cocoa text-sm font-medium">
                {t(`portal.dashboard.aiState.${status === "ai_pre_analysis" ? "running" : status === "draft" || status === "submitted" ? "pending" : "done"}`)}
              </p>
              <p className="text-warmGrey text-xs mt-0.5">{t("portal.dashboard.aiHint")}</p>
            </div>
          </div>
        </Card>

        {/* Profile completeness */}
        <Card
          accent="butter"
          eyebrow={t("portal.dashboard.profileEyebrow")}
          title={t("portal.dashboard.profileTitle", { p: completeness?.percentage ?? 0 })}
          action={
            <Link
              to="/portal/startup/profile"
              className="text-xs text-raspberry hover:text-coral inline-flex items-center gap-1"
              data-testid="profile-card-link"
            >
              {t("portal.dashboard.profileEdit")} <ChevronRight size={14} />
            </Link>
          }
          testId="card-profile"
        >
          <div className="w-full h-2 rounded-full bg-ivory overflow-hidden">
            <div
              className="h-full bg-raspberry transition-all duration-700"
              style={{ width: `${completeness?.percentage ?? 0}%` }}
            />
          </div>
          {completeness?.missing_fields?.length ? (
            <p className="text-xs text-warmGrey mt-3">
              {t("portal.dashboard.profileMissing")}:{" "}
              {completeness.missing_fields.slice(0, 4).map((f) => t(`portal.fields.${f}`)).join(" · ")}
              {completeness.missing_fields.length > 4 && " …"}
            </p>
          ) : (
            <p className="text-xs text-warmGrey mt-3">{t("portal.dashboard.profileComplete")}</p>
          )}
        </Card>
      </div>

      {/* Recent activity */}
      <Card
        eyebrow={t("portal.dashboard.activityEyebrow")}
        title={t("portal.dashboard.activityTitle")}
        action={
          <Link
            to="/portal/startup/timeline"
            className="text-xs text-raspberry hover:text-coral inline-flex items-center gap-1"
            data-testid="activity-card-link"
          >
            {t("portal.dashboard.activityAll")} <ChevronRight size={14} />
          </Link>
        }
        testId="card-activity"
      >
        {timeline.length === 0 ? (
          <p className="text-warmGrey text-sm">{t("portal.dashboard.activityEmpty")}</p>
        ) : (
          <ul className="flex flex-col gap-3 mt-1">
            {timeline.slice(0, 5).map((ev, idx) => (
              <li key={ev.id || idx} className="flex items-start gap-3" data-testid={`activity-${idx}`}>
                <span className="mt-1 w-2 h-2 rounded-full bg-sageMint shrink-0" aria-hidden="true" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-cocoa">
                    {t(`portal.events.${ev.event_type}`, { defaultValue: ev.event_type })}
                  </p>
                  <p className="text-xs text-warmGrey">{formatDate(ev.timestamp, lang)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Phase stubs */}
      <div className="grid sm:grid-cols-2 gap-5">
        <Card accent="sage" eyebrow={t("portal.phases.phase8")} title={t("portal.dashboard.matchesTitle")} testId="card-matches-stub">
          <p className="text-warmGrey text-sm leading-relaxed">{t("portal.dashboard.matchesBody")}</p>
          <div className="mt-4 rounded-2xl bg-sageMint/40 p-4 flex items-center gap-3">
            <Sparkles size={16} className="text-raspberry" />
            <p className="text-xs text-cocoa">{t("portal.dashboard.matchesNote")}</p>
          </div>
        </Card>
        <Card accent="lavender" eyebrow={t("portal.phases.phase10")} title={t("portal.dashboard.messagesTitle")} testId="card-messages-stub">
          <p className="text-warmGrey text-sm leading-relaxed">{t("portal.dashboard.messagesBody")}</p>
          <div className="mt-4 rounded-2xl bg-lavenderFog/40 p-4 flex items-center gap-3">
            <MessageSquare size={16} className="text-raspberry" />
            <p className="text-xs text-cocoa">{t("portal.dashboard.messagesNote")}</p>
          </div>
        </Card>
      </div>
    </div>
  );
}
